import React, { useState } from 'react';
import { User, Users } from 'lucide-react';
import ProfileTab from './components/ProfileTab';
import CompanyTab from './components/CompanyTab';
import TeamTab from './components/TeamTab';
import PipelineTab from './components/PipelineTab';
import CustomFieldsTab from './components/CustomFieldsTab';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import { hasPermission } from '../../utils/permissions';



export default function Settings() {
  const { profile, isSuperAdmin } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'company', label: 'Company', icon: 'Building2', permission: { feature: 'settings', level: 'write' } },
    { id: 'team', label: 'Team', icon: 'Users', permission: { feature: 'users', level: 'read' } },
    { id: 'pipeline', label: 'Pipeline', icon: 'GitBranch', permission: { feature: 'settings', level: 'write' } },
    { id: 'custom-fields', label: 'Custom Fields', icon: 'Settings', permission: { feature: 'settings', level: 'admin' } },
  ];

  const getFilteredTabs = () => {
    return tabs?.filter(tab => {
      if (!tab?.permission) return true;
      return hasPermission(profile?.roles, tab?.permission?.feature, tab?.permission?.level);
    });
  };

  const filteredTabs = getFilteredTabs();

  return (
    <div className="flex h-screen bg-gray-50">
      <ComponentIdBadge 
        id="pg-settings-main" 
        name="Settings Main Page"
        description="Application settings and configuration page"
        position="top-right"
      />
      <Sidebar onClose={handleSidebarClose} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={handleSidebarToggle} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto p-6">
            <div className="mb-8 relative">
              <ComponentIdBadge 
                id="hdr-settings-title" 
                name="Settings Page Header"
                description="Page title and settings navigation"
                position="inline"
                showLabel={true}
              />
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
              <p className="text-gray-600">Manage your account and application preferences</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="relative lg:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200 p-4 h-fit">
                <ComponentIdBadge 
                  id="nav-settings-tabs" 
                  name="Settings Navigation Tabs"
                  description="Tab navigation for different settings sections"
                />
                <div className="space-y-2">
                  {filteredTabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      {tab?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <ComponentIdBadge 
                  id="cmp-settings-content" 
                  name="Settings Content Area"
                  description="Dynamic content area for active settings tab"
                />
                {activeTab === 'profile' && <ProfileTab />}
                {activeTab === 'company' && <CompanyTab />}
                {activeTab === 'team' && <TeamTab />}
                {activeTab === 'pipeline' && <PipelineTab />}
                {activeTab === 'custom-fields' && <CustomFieldsTab />}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}